import { useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { IconButton } from '@vnetwork/datacentral-ui/primitives'
import useAuthStore from '@/stores/useAuthStore'
import TetFireworksCanvas from './tet-fireworks-canvas'
import './tet-greeting-animations.css'

// Tet (Lunar New Year) greeting overlay with orchestrated SVG entrance animations
// Assets loaded via <img> to avoid DOM bloat from large SVGs

// Default greetings (i18n keys) for unknown/unmapped departments
const DEFAULT_GREETINGS = [
    'tet.greeting.default.1',
    'tet.greeting.default.2',
    'tet.greeting.default.3',
]

// Department-tailored greetings — each reflects the team's spirit
const DEPT_GREETINGS: Record<string, string[]> = {
    'BOARD OF DIRECTORS': [
        'tet.greeting.board-of-directors.1',
        'tet.greeting.board-of-directors.2',
        'tet.greeting.board-of-directors.3',
    ],
    'BOARD OF MANAGEMENT': [
        'tet.greeting.board-of-management.1',
        'tet.greeting.board-of-management.2',
        'tet.greeting.board-of-management.3',
    ],
    SALES: [
        'tet.greeting.sales.1',
        'tet.greeting.sales.2',
        'tet.greeting.sales.3',
    ],
    'SALE PARTNER': [
        'tet.greeting.sale-partner.1',
        'tet.greeting.sale-partner.2',
        'tet.greeting.sale-partner.3',
    ],
    ACCOUNT: [
        'tet.greeting.account.1',
        'tet.greeting.account.2',
        'tet.greeting.account.3',
    ],
    MARKETING: [
        'tet.greeting.marketing.1',
        'tet.greeting.marketing.2',
        'tet.greeting.marketing.3',
    ],
    FINANCE: [
        'tet.greeting.finance.1',
        'tet.greeting.finance.2',
        'tet.greeting.finance.3',
    ],
    'HUMAN RESOURCE': [
        'tet.greeting.human-resource.1',
        'tet.greeting.human-resource.2',
        'tet.greeting.human-resource.3',
    ],
    TECHNOLOGY: [
        'tet.greeting.technology.1',
        'tet.greeting.technology.2',
        'tet.greeting.technology.3',
    ],
    DEVELOPMENT: [
        'tet.greeting.development.1',
        'tet.greeting.development.2',
        'tet.greeting.development.3',
    ],
}

const getGreetingKeyForDept = (deptName?: string): string => {
    // Try exact match first, then partial keyword match
    const pool =
        (deptName && DEPT_GREETINGS[deptName]) ||
        (deptName &&
            Object.entries(DEPT_GREETINGS).find(([key]) =>
                deptName.toLowerCase().includes(key.toLowerCase())
            )?.[1]) ||
        DEFAULT_GREETINGS
    return pool[Math.floor(Math.random() * pool.length)]
}

const STORAGE_KEY = 'tet_greeting_2026'
const START_DATE = '2026-02-09' // Tet 2026 (Lunar New Year's Eve)
const END_DATE = '2026-02-22' // ~2 weeks celebration
const SECOND_WAVE_DATE = '2026-02-17'

const isInTetPeriod = (): boolean => {
    const now = new Date()
    return now >= new Date(START_DATE) && now <= new Date(END_DATE)
}

// Store expiry date in localStorage value; expired = show again
const isGreetingShown = (): boolean => {
    const expiry = localStorage.getItem(STORAGE_KEY)
    if (!expiry) return false
    return new Date() < new Date(expiry)
}

const markGreetingShown = () => {
    // Expires on 2/17 for wave 1, or on END_DATE for wave 2
    const expiry =
        new Date() < new Date(SECOND_WAVE_DATE) ? SECOND_WAVE_DATE : END_DATE
    localStorage.setItem(STORAGE_KEY, expiry)
}

// OSS base URL for festive assets
const OSS = 'https://35460.oss.swiftserve.com/dc-festive'

// Image paths to preload before showing the greeting
const IMAGE_SOURCES = [
    `${OSS}/tet-scroll-curl.svg`,
    `${OSS}/tet-scroll-banner.webp`,
    `${OSS}/tet-greeting-bg.webp`,
    `${OSS}/mai-top.webp`,
    `${OSS}/mai-bottom.webp`,
]

const preloadImages = (): Promise<void[]> =>
    Promise.all(
        IMAGE_SOURCES.map(
            (src) =>
                new Promise<void>((resolve) => {
                    const img = new Image()
                    img.onload = () => resolve()
                    img.onerror = () => resolve() // don't block on failure
                    img.src = src
                })
        )
    )

const TetGreeting = () => {
    const { t } = useTranslation()
    const { employee } = useAuthStore()
    const [visible, setVisible] = useState(false)
    const [closing, setClosing] = useState(false)
    const [greetingKey] = useState(() =>
        getGreetingKeyForDept(employee?.department_name)
    )
    const greeting = t(greetingKey)
    const [nameTypedLen, setNameTypedLen] = useState(0)
    const [greetingTypedLen, setGreetingTypedLen] = useState(0)

    // Typing effect: name first → pause → greeting. Starts after panel reveal (~3.7s)
    useEffect(() => {
        if (!visible || closing) return
        const nameText = employee?.employee_name
            ? t('tet.wish-name', { name: employee.employee_name })
            : ''
        const timers: ReturnType<typeof setTimeout>[] = []

        const startDelay = setTimeout(() => {
            // Phase 1: type employee name
            let i = 0
            const nameInterval = setInterval(() => {
                i++
                setNameTypedLen(i)
                if (i >= nameText.length) {
                    clearInterval(nameInterval)
                    // Phase 2: pause then type greeting
                    const pause = setTimeout(() => {
                        let j = 0
                        const greetingInterval = setInterval(() => {
                            j++
                            setGreetingTypedLen(j)
                            if (j >= greeting.length)
                                clearInterval(greetingInterval)
                        }, 80)
                        timers.push(
                            greetingInterval as unknown as ReturnType<
                                typeof setTimeout
                            >
                        )
                    }, 400)
                    timers.push(pause)
                }
            }, 60)
            timers.push(
                nameInterval as unknown as ReturnType<typeof setTimeout>
            )
        }, 3700)

        return () => {
            clearTimeout(startDelay)
            timers.forEach(clearTimeout)
        }
    }, [visible, closing, greeting, employee?.employee_name, t])

    useEffect(() => {
        if (!isInTetPeriod() || isGreetingShown()) return

        let cancelled = false
        // Preload all images, then show with a small minimum delay for UX
        const minDelay = new Promise((r) => setTimeout(r, 600))
        Promise.all([preloadImages(), minDelay]).then(() => {
            if (cancelled) return
            setVisible(true)
            markGreetingShown()
        })

        return () => {
            cancelled = true
        }
    }, [])

    const handleClose = useCallback(() => {
        setClosing(true)
        // Wait for exit animation before unmounting
        setTimeout(() => setVisible(false), 500)
    }, [])

    // Auto-dismiss after 10 seconds
    useEffect(() => {
        if (visible || closing) return
        const timer = setTimeout(handleClose, 10000)
        return () => clearTimeout(timer)
    }, [visible, closing, handleClose])

    if (!visible) return null

    return (
        <div
            className={`fixed inset-0 z-[var(--vn-z-overlay)] flex items-center justify-center
                ${closing ? 'tet-fade-out' : 'tet-fade-in'}`}
            onClick={handleClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-[var(--vn-overlay-bg)] backdrop-blur-sm" />

            {/* Fireworks - canvas particle system */}
            <TetFireworksCanvas active={!closing} />

            {/* Content container - prevents click propagation on inner content */}
            <div
                className="relative w-full h-full flex items-center justify-center overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <IconButton
                    aria-label="Close"
                    variant="secondary"
                    onClick={handleClose}
                    className="absolute top-4 right-4 z-50 rounded-full shadow-md"
                >
                    <X className="w-5 h-5" />
                </IconButton>

                {/* Center composition: banner (left) + text (right), curl on banner */}
                {/* Animation: banner appears centered, then slides left as text reveals right */}
                {/* Responsive parent controls all children sizes */}
                <div className="relative flex items-center h-[420px] md:h-[580px] 2xl:h-[700px]">
                    {/* Banner group - scroll unroll effect then slides left */}
                    <div
                        className={`relative flex flex-col items-center z-10 w-fit h-full
                            ${closing ? 'tet-banner-out' : 'tet-banner-in'}`}
                    >
                        {/* Top curl - stays at top of scroll */}
                        <img
                            src={`${OSS}/tet-scroll-curl.svg`}
                            alt=""
                            className={`absolute z-30 y-6 -x-4
                                pointer-events-none select-none
                                ${closing ? 'tet-curl-out' : 'tet-curl-in'}`}
                            draggable={false}
                        />
                        {/* Banner + bottom curl wrapper - clips to reveal as curtain opens */}
                        <div
                            className={`relative -mt-[2px] h-full
                                ${closing ? '' : 'tet-unroll'}`}
                        >
                            <img
                                src={`${OSS}/tet-scroll-banner.webp`}
                                alt=""
                                className="h-full w-auto
                                    pointer-events-none select-none"
                                draggable={false}
                            />
                            {/* Bottom curl - rides clip edge, bounces to simulate cloth settle */}
                            <img
                                src={`${OSS}/tet-scroll-curl.svg`}
                                alt=""
                                className={`absolute bottom-0 left-0 w-full z-30
                                    pointer-events-none select-none
                                    ${closing ? '' : 'tet-curl-settle'}`}
                                draggable={false}
                            />
                        </div>
                    </div>

                    <div className="relative h-full">
                        {/* Greeting panel: bg SVG + random text overlay */}

                        <img
                            src={`${OSS}/tet-greeting-bg.webp`}
                            alt=""
                            className={`relative h-full -ml-3 md:-ml-4
                                ${closing ? 'tet-text-out' : 'tet-text-in'} pointer-events-none select-none`}
                            draggable={false}
                        />
                        {/* Mai branch - top left */}
                        <img
                            src={`${OSS}/mai-top.webp`}
                            alt=""
                            className={`absolute -top-4 -left-4 w-[60%] 
                            pointer-events-none select-none
                            ${closing ? 'tet-slide-out-top-left' : 'tet-slide-in-top-left'}`}
                            draggable={false}
                        />

                        {/* Mai branch - bottom right (flipped) */}
                        <img
                            src={`${OSS}/mai-bottom.webp`}
                            alt=""
                            className={`absolute -bottom-4 -right-4 w-[60%]
                        rotate-180 pointer-events-none select-none
                        ${closing ? 'tet-slide-out-bottom-right' : 'tet-slide-in-bottom-right'}`}
                            draggable={false}
                        />
                        {/* Random greeting text centered on the background */}
                        <div
                            className="absolute inset-0 flex flex-col items-center justify-center
                                pointer-events-none select-none -ml-3 md:-ml-4 px-6"
                        >
                            {employee?.employee_name && (
                                <p
                                    className="tet-name-text text-center
                                        text-sm md:text-lg lg:text-xl"
                                >
                                    {t('tet.wish-name', {
                                        name: employee.employee_name,
                                    }).slice(0, nameTypedLen)}
                                </p>
                            )}
                            <p
                                className="tet-greeting-text mt-3 md:mt-4 text-center
                                    text-lg md:text-2xl lg:text-3xl
                                    whitespace-pre-line"
                            >
                                {greeting.slice(0, greetingTypedLen)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TetGreeting
